import React, { useState, useCallback } from "react";
import { isEqual } from "lodash";
import Link from "@components/link";
import OptionSelector from "@components/product/option-selector";
import ShippingConditions from "@components/product/shipping-conditions";
import Button from "../button";
import { useQuickViewStore, useProductStore } from "@src/hooks/useStore";
import { formatPrice, getPricesFromContextualPricing } from "@src/utils/products/pricing";
import { getRegionCurrency } from "@src/utils/region";
import copyIcon from "@svgs/copy-icon.svg"
import { isVisibleIn } from "@utils/region"

const ProductAccordionItem = ({
  product,
  currentVariant,
  index,
  isOpen,
  setActiveAccordion,
  updateSelectedVariants,
  setCurrentProductVariant,
  revolPId,
  selectedVariants,
  layout,
  crateSize,
  sanityDiscountCodes
}) => {

  const {
    setQuickViewOpen,
    setQuickViewProduct,
    setQuickViewVariant,
    setQuickViewType,
  } = useQuickViewStore(["setQuickViewOpen", "setQuickViewProduct", "setQuickViewVariant", "setQuickViewType"]);
  const { setCurrentVariant } = useProductStore(["setCurrentVariant"]);
  const [isConfirmedOption, setIsConfirmedOption] = useState(false);
  const { discountAmount, discountQuantity, discountType, isOptional } = product;
  const optionalItemAdded = selectedVariants?.find(variant => variant?.productId === product?.shopifyId)?.selected;
  const isUnavailable = !currentVariant?.availableForSale;
  const currentOptions = [...currentVariant?.selectedOptions];
  const [copied, setCopied] = useState(false)

  const handleAddToBundle = (selected) => {
    const selectedVariant = product.variants
      .map(variant => ({ ...variant, selected: false, isOptional }))
      .find(({ selectedOptions }) =>
        isEqual(currentOptions, selectedOptions)
      );
    
    const isRevol = selectedVariant?.productId === revolPId;

    updateSelectedVariants(index, { ...selectedVariant, selected }, isRevol);
  }

  const handleOptionChange = (optionIndex, { target }) => {
    const { value } = target

    if (currentOptions[optionIndex].value !== value) {
      currentOptions[optionIndex] = {
        ...currentOptions[optionIndex],
        value,
      }

      const selectedVariant = product.variants
        .map(variant => ({ ...variant, selected: !isOptional, isOptional }))
        .find(({ selectedOptions }) =>
          isEqual(currentOptions, selectedOptions)
        );
      
      const isRevol = selectedVariant?.productId === revolPId;

      updateSelectedVariants(index, { ...selectedVariant, isMainProduct: product.isMainProduct }, isRevol);
      setCurrentProductVariant({ ...selectedVariant })
      setCurrentVariant({ ...selectedVariant }, product, sanityDiscountCodes)
      setIsConfirmedOption(true);
    }
  }

  const openProductQuickView = () => {
    setQuickViewProduct(product);
    setQuickViewVariant(currentVariant)
    setQuickViewOpen(true);
    setQuickViewType('details');
  };

  const isFree = discountType === "percentage" 
    ? discountAmount === 100 
    : discountAmount === +currentVariant?.price;

  const discountLabel = isFree 
    ? "Free" 
    : discountType === "percentage" 
      ? `${discountAmount}% off` 
      : `$${discountAmount} off`;

  const bundleTitle = product?.titleForBundle || product?.title;

  const getDiscountedAmount = (price) => product?.discountType === "percentage" ? price - (price * (+product?.discountAmount / 100)) : price - +product?.discountAmount;

  const { price, compareAtPrice, currency } = getPricesFromContextualPricing(currentVariant?.prices);

  const discountedPriceValue = isFree 
    ? "FREE"
    : formatPrice({ price: getDiscountedAmount(+price * discountQuantity), currency: getRegionCurrency() || currency });

  const discountedCompareAtPriceValue = compareAtPrice > price 
    ? formatPrice({ price: compareAtPrice, currency: getRegionCurrency() || currency })
    : formatPrice({ price: price, currency: getRegionCurrency() || currency });

    const extraDiscountRegex = /:\s*(.*)$/

    const extraDiscountMessage =
    currentVariant?.extraDiscountMessage || product?.extraDiscountMessage

    const hasSizeGuide = crateSize?.sizes?.length > 0

    const detectScriptPart = extraDiscountMessage
      ?.replace(/[*_]/g, "")
      ?.match(extraDiscountRegex)
  
    const extraDiscountCode = detectScriptPart && detectScriptPart[1]

    const extraDiscountText =
    extraDiscountCode && extraDiscountMessage?.replace(extraDiscountCode, "")

    const handleCopyClick = e => {
      e.preventDefault()
      navigator.clipboard.writeText(extraDiscountCode)
      setCopied(true)
  
      setTimeout(() => {
        setCopied(false)
      }, 2000)
    }

    const extraDiscount = () =>
        extraDiscountMessage && (
          <button
            onClick={handleCopyClick}
            className="relative inline-block gap-1 items-center w-full max-md:flex max-md:justify-center md:items-start text-left bg-steadfast-red bg-opacity-20 rounded-lg cursor-pointer px-3 pt-1.5 pb-1.5 text-soft-black font-light text-sm"
          >
            <div className="flex items-center gap-1 shrink-0">
              <span className="inline">
                {extraDiscountText}
                <span className="font-medium">{extraDiscountCode}</span>
              </span>
              <img className="h-4 ml-2" src={copyIcon} alt="Copy" />
            </div>
            <div
              style={{
                transition: "opacity 350ms ease-out",
                opacity: copied ? "1" : "0",
                pointerEvents: copied ? "auto" : "none",
              }}
              className="arrow-box-down flex justify-end bg-black/70 rounded-sm absolute top-[-70%] right-0 transition-transform"
            >
              <p className="text-sm text-[#ffffff] p-1">Copied to clipboard</p>
            </div>
          </button>
        )

    const sizeGuide = () =>
      (crateSize && product.isMainProduct) &&
      crateSize.quizLink && (
        <div className="is-sticky-hidden">
          <Link
            to={crateSize.quizLink}
            className="font-medium underline"
            target="_blank"
            rel="noreferrer"
          >
            Take the Sizing Quiz
          </Link>
        </div>
      )
      
  if (!isVisibleIn(product.visibilityRegions)) return null;

  if (layout == "product") return (
    <>
    <ProductAccordionInner 
      product={product}
      currentVariant={currentVariant}
      handleOptionChange={handleOptionChange}
      isOptional={isOptional}
      discountedCompareAtPriceValue={discountedCompareAtPriceValue}
      discountedPriceValue={discountedPriceValue}
      layout={layout}
      crateSize={crateSize}
    />
    {(hasSizeGuide || extraDiscountMessage) && (
        <div className="gap-5 flex justify-between items-center">
            <div className="max-md:hidden max-w-[60%]">{extraDiscount()}</div>
            {sizeGuide()}
      </div>
    )}
   </>
  );

  return (
    <div className="flex flex-col rounded-xl border border-gray p-4 max-w-full">
      <dt>
        <button
          onClick={() => {
            setActiveAccordion(index, product, currentVariant)
            setIsConfirmedOption(true)
          }}
          className={`flex items-center justify-between w-full text-left font-medium focus:outline-none ${!isOpen && !isConfirmedOption ? 'text-soft-black' : optionalItemAdded && 'text-safety-green'}`}
        >
          <div className="flex items-center justify-between flex-grow font-medium">
            <h2 className="text-base antialiased text-soft-black flex items-center">
              {isOptional 
                ? <span className={`text-white w-6 h-6 rounded-md justify-center items-center text-base mr-3 xl:mr-5 aspect-1 ${optionalItemAdded ? 'bg-safety-green' : 'bg-soft-black'}`}>
                    {index + 1}
                  </span>
                : <span className={`text-white w-6 h-6 rounded-md justify-center items-center text-base mr-3 xl:mr-5 aspect-1 ${!isOpen && !isConfirmedOption ? 'bg-soft-black' : 'bg-safety-green' }`}>
                    {index + 1}
                  </span>
              }
              <span className="flex items-center gap-2 flex-wrap">
                {bundleTitle}
                {discountAmount > 0 && 
                  <span className="text-safety-green text-xs py-1.5 px-4 bg-safety-green bg-opacity-20 rounded-lg leading-none">
                    {discountLabel}
                  </span>
                }
                {isOptional 
                  && <span className="text-input-gray font-normal text-xs">{"(Optional)"}</span>
                }
              </span>
            </h2>
          </div>
          {isOptional
            ? <span className="flex items-center ml-2 text-right shrink-0">
                {optionalItemAdded ? "Added" : "Select Options"}
              </span>
            : <span className="flex items-center ml-2 text-right shrink-0">
                {!isOpen && !isConfirmedOption && "Select options"}
                {isOpen ? "Confirm Options" : isConfirmedOption && "Change Options"}
              </span>
          }
        </button>
      </dt>
      {isOpen && (
        <dd
          className="transition-[height] ease-out ease-in-out overflow-hidden pt-4"
          style={{ height: isOpen ? "auto" : "0" }}
        >
          <ProductAccordionInner 
            product={product}
            currentVariant={currentVariant}
            handleOptionChange={handleOptionChange}
            isOptional={isOptional}
            discountedPriceValue={discountedPriceValue}
          />
          <ShippingConditions 
            {...{
              productEntry: product,
              currentEntryVariant: currentVariant,
              hideBackorderDetails: true,
            }} 
            className="mt-2" />
          {isOptional && 
            <Button 
              ctaWidth={"full"}
              onClick={() => handleAddToBundle(!optionalItemAdded)} 
              theme={optionalItemAdded ? "btn-transparent-dark" : "btn-secondary"} 
              arrow={"none"} 
              className={"mt-5 flex justify-center gap-x-1.5 items-center"}
              disabled={isUnavailable}
            >
              {optionalItemAdded ? "Remove from Bundle" : "Add to Bundle"}
            </Button>
          }          
          <div className="w-full flex justify-between items-center">
            <Link onClick={openProductQuickView} className="text-safety-green font-medium underline pt-3">
              Quick view
            </Link>
          </div>
          {discountQuantity > 1 &&
            <div className={`h-full w-full flex flex-col gap-y-2.5 overflow-hidden transition-all duration-300 mt-2.5`}>
              {discountQuantity && <div className="w-full p-4 text-base flex justify-between items-center bg-light-gray rounded-xl font-medium">
                Quantity
                <span className="text-safety-green">{discountQuantity}</span>
              </div>}
            </div>
          }
        </dd>
      )}
    </div>
  );
};

const ProductAccordionInner = ({
  product,
  currentVariant,
  handleOptionChange,
  isOptional,
  discountedPriceValue,
  discountedCompareAtPriceValue,
  layout
}) => {
  
  return (
    <div className="flex gap-x-5 justify-between">
      <OptionSelector
        currentVariant={currentVariant}
        options={product.options}
        productOptions={product.productOptions}
        variants={product.variants}
        handleOptionChange={handleOptionChange}
        productTitle={product?.titleForBundle || product?.title}
        isMainProduct={product.isMainProduct}
        layout={layout}
      />
      {isOptional && (
        <div className="w-fit flex flex-col text-right justify-start items-end pr-1">
          <span className="subquote text-[16px] text-darker-gray relative px-1 after:flex after:user-select-none after:border-b-1 after:w-full after:border-solid after:border-black after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2">{discountedCompareAtPriceValue && discountedCompareAtPriceValue}</span>
          <span className="headline text-[24px] leading-none">{discountedPriceValue && discountedPriceValue}</span>
        </div>
      )}
    </div>
  )
}

const ProductAccordion = ({
  bundleProductsList,
  setCurrentProductVariant,
  updateSelectedVariants,
  selectedVariants,
  revolPId,
  mainLayout,
  crateSize,
  sanityDiscountCodes
}) => {
  const [activeAccordionIndex, setActiveIndex] = useState(null);

  const setActiveAccordion = useCallback(
    (index, product, currentVariant) => {
      setActiveIndex(activeAccordionIndex === index ? null : index);
      if (activeAccordionIndex !== index) {
        setCurrentProductVariant({ ...currentVariant });
      }
    },
    [activeAccordionIndex]
  );


  return (
    <div className="flex flex-col gap-4">
      {bundleProductsList.map((product, index) => {
        const isOpen = activeAccordionIndex === index;
        const currentVariant =
          selectedVariants?.find(
            (variant, _index) =>
              _index === index && variant?.productId === product?.shopifyId
          ) || product?.variants?.[0];
                
        return (
          <ProductAccordionItem
            key={`${product.shopifyId}-${index}`}
            product={product}
            selectedVariants={selectedVariants}
            currentVariant={currentVariant}
            index={index}
            isOpen={isOpen}
            setActiveAccordion={setActiveAccordion}
            updateSelectedVariants={updateSelectedVariants}
            setCurrentProductVariant={setCurrentProductVariant}
            revolPId={revolPId}
            layout={mainLayout}
            crateSize={crateSize}
            sanityDiscountCodes={sanityDiscountCodes}
          />
        );
      })}
    </div>
  );
};

export default ProductAccordion;